/**
 * Vendor Application actions
 */
import Constants from '../../constants';

const { Vendor } = Constants;

/**
 * Action object dispatched to update vendor
 *
 * @param {string} userId the user id
 * @param {object} formData the key value pair for updating vendor fields
 * @param {boolean} fileUpload indicates whether file is uploaded or not
 */
const updateVendor = (userId, formData, fileUpload = false) => ({
  type: Vendor.UPDATE_VENDOR,
  formData,
  userId,
  fileUpload,
});

/**
 * Action object dispatched to request update vendor banking
 *
 * @param {string} userId the user id
 * @param {object} formData the key value pair for updating banking vendor fields
 */
const updateVendorBanking = (userId, formData) => ({
  type: Vendor.UPDATE_VENDOR_BANKING,
  formData,
  userId,
});

/**
 * Action object dispatched to update vendor on file progress
 *
 * @param {object} progressEvent the progress event object
 * @param {string} fileName the name of the file concerned
 */
const updateVendorFileProgress = (progressEvent, fileName) => ({
  type: Vendor.UPDATE_VENDOR_FILE_PROGRESS,
  progressEvent,
  fileName,
});

/**
 * Action object dispatched when updating vendor errors
 *
 * @param {object} error error object
 */
const updateVendorError = (error) => ({
  type: Vendor.UPDATE_VENDOR_ERROR,
  error,
});

/**
 * Action object dispatched when updating vendor succeeds
 *
 * @param {object} vendor data object of the vendor to be updated
 */
const updateVendorSuccess = ({ vendor }) => ({
  type: Vendor.UPDATE_VENDOR_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to get public files
 * @param {string} getType either 'admin' or 'user'
 */
const getPublicFiles = (getType) => ({
  type: Vendor.GET_PUBLIC_FILES,
  getType,
});

/**
 * Action object dispatched when fetching public files errors
 *
 */
const getPublicFilesError = (error) => ({
  type: Vendor.GET_PUBLIC_FILES_ERROR,
  error,
});

/**
 * Action object dispatched when fetching public files succeeds
 *
 */
const getPublicFilesSuccess = (publicFilesUrls) => ({
  type: Vendor.GET_PUBLIC_FILES_SUCCESS,
  publicFilesUrls,
});

/**
 * Action object dispatched to get vendor
 *
 * @param {string} userId the user id
 */
const getVendor = (userId) => ({
  type: Vendor.GET_VENDOR,
  userId,
});

/**
 * Action object dispatched when fetching vendor errors
 *
 * @param {object} error error object
 */
const getVendorError = (error) => ({
  type: Vendor.GET_VENDOR_ERROR,
  error,
});

/**
 * Action object dispatched when fetching vendor succeeds
 *
 * @param {object} vendor vendor data object
 */
const getVendorSuccess = ({ vendor }) => ({
  type: Vendor.GET_VENDOR_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to create vendor in Netsuite
 *
 * @param {string} userId the user id
 */
const createVendorNetSuite = (userId) => ({
  type: Vendor.CREATE_VENDOR_NETSUITE,
  userId,
});

/**
 * Action object dispatched when creating vendor in Netsuite errors
 *
 * @param {object} error error object
 */
const createVendorNetSuiteError = (error) => ({
  type: Vendor.CREATE_VENDOR_NETSUITE_ERROR,
  error,
});

/**
 * Action object dispatched when creating vendor in Netsuite succeeds
 *
 * @param {object} vendor vendor data object
 */
const createVendorNetSuiteSuccess = ({ vendor }) => ({
  type: Vendor.CREATE_VENDOR_NETSUITE_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to create vendor contact
 *
 * @param {string} userId the user id
 */
const createVendorContact = (userId, contact, reInstanciateDeletedUser) => ({
  type: Vendor.CREATE_VENDOR_CONTACT,
  userId,
  contact,
  reInstanciateDeletedUser,
});

/**
 * Action object dispatched when create vendor contact errors
 *
 * @param {object} error error object
 */
const createVendorContactError = (error) => ({
  type: Vendor.CREATE_VENDOR_CONTACT_ERROR,
  error,
});

/**
 * Action object dispatched when create vendor contact succeeds
 *
 * @param {object} vendor vendor data object
 */
const createVendorContactSuccess = (vendor) => ({
  type: Vendor.CREATE_VENDOR_CONTACT_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to update vendor in Netsuite
 *
 * @param {string} userId the user id
 * @param {object} vendor  vendor updated properties
 */
const updateVendorNetSuite = (userId, vendor) => ({
  type: Vendor.UPDATE_VENDOR_NETSUITE,
  userId,
  vendor,
});

/**
 * Action object dispatched when updating vendor in Netsuite errors
 *
 * @param {object} error error object
 */
const updateVendorNetSuiteError = (error) => ({
  type: Vendor.UPDATE_VENDOR_NETSUITE_ERROR,
  error,
});

/**
 * Action object dispatched when updating vendor in Netsuite succeeds
 *
 * @param {object} vendor vendor data object
 */
const updateVendorNetSuiteSuccess = ({ vendor }) => ({
  type: Vendor.UPDATE_VENDOR_NETSUITE_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to update vendor contact
 *
 * @param {string} userId the user id
 */
const updateVendorContact = (userId, contactId, updates) => ({
  type: Vendor.UPDATE_VENDOR_CONTACT,
  userId,
  contactId,
  updates,
});

/**
 * Action object dispatched when updating vendor contact errors
 *
 * @param {object} error error object
 */
const updateVendorContactError = (error) => ({
  type: Vendor.UPDATE_VENDOR_CONTACT_ERROR,
  error,
});

/**
 * Action object dispatched when updating vendor contact succeeds
 *
 * @param {string} contactId - id of the vendor contact
 * @param {object} vendor vendor data object
 */
const updateVendorContactSuccess = (vendor) => ({
  type: Vendor.UPDATE_VENDOR_CONTACT_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to update vendor contact
 *
 * @param {string} userId the user id
 * @param {string} contactId - id of the vendor contact
 */
const removeVendorContact = (userId, contactId) => ({
  type: Vendor.REMOVE_VENDOR_CONTACT,
  userId,
  contactId,
});

/**
 * Action object dispatched when updating vendor contact errors
 *
 * @param {object} error object
 */
const removeVendorContactError = (error) => ({
  type: Vendor.REMOVE_VENDOR_CONTACT_ERROR,
  error,
});

/**
 * Action object dispatched when updating vendor contact succeeds
 *
 * @param {object} vendor vendor data object
 */
const removeVendorContactSuccess = (vendor) => ({
  type: Vendor.REMOVE_VENDOR_CONTACT_SUCCESS,
  vendor,
});

/**
 * Action object dispatched to submit vendor
 *
 * @param {string} userId the user id
 */
const submitVendor = ({ userId }) => ({
  type: Vendor.SUBMIT_VENDOR,
  userId,
});

/**
 * Action object dispatched when submitting vendor errors
 *
 * @param {object} error object
 */
const submitVendorError = (error) => ({
  type: Vendor.SUBMIT_VENDOR_ERROR,
  error,
});

/**
 * Action object dispatched when submitting vendor succeeds
 *
 * @param {object} vendor data object
 */
const submitVendorSuccess = (vendor) => ({
  type: Vendor.SUBMIT_VENDOR_SUCCESS,
  vendor,
});

export default {
  updateVendor,
  updateVendorBanking,
  updateVendorError,
  updateVendorSuccess,
  updateVendorFileProgress,
  getPublicFiles,
  getPublicFilesError,
  getPublicFilesSuccess,
  getVendor,
  getVendorError,
  getVendorSuccess,
  createVendorNetSuite,
  createVendorNetSuiteError,
  createVendorNetSuiteSuccess,
  createVendorContact,
  createVendorContactError,
  createVendorContactSuccess,
  updateVendorContact,
  updateVendorContactError,
  updateVendorContactSuccess,
  updateVendorNetSuite,
  updateVendorNetSuiteError,
  updateVendorNetSuiteSuccess,
  removeVendorContact,
  removeVendorContactError,
  removeVendorContactSuccess,
  submitVendor,
  submitVendorError,
  submitVendorSuccess,
};
